// components/UploadFileLoader.jsx
import React, { useEffect, useContext, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { Button, CircularProgress, IconButton } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DoNotDisturbOnIcon from "@mui/icons-material/DoNotDisturbOn";
import CancelIcon from "@mui/icons-material/Cancel";
import CloseIcon from "@mui/icons-material/Close";
import ReUploadIcon from "assets/images/svg-icons/reUploadIcon.svg"
import { CancelUploadModal, MuiBox, MuiTypography } from "components/atoms";
import { useTranslation } from "react-i18next";
import {
  cancelUploadingAllFiles,
  cancelUploadingFile,
  clearUploadFiles,
} from "store/slices/general";
import { abortAllUploads, abortUpload } from "utils/uploadController";
import { UploadFilesAction } from "store/middlewares/files";
import { getFileExtension } from "utils/fileExtension";
import { FilterContext } from "context/FilterContext";
import { FirebaseContext } from "context/FirebaseContext";
import { openToast } from "store/slices/toast.slice";
import { closeCenterModal, openCenterModal } from "store/slices/CenterModal.slice";

const UploadFileLoader = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { folderId } = useParams();
  const { uploadNewFile } = useSelector((state) => state.general);
  const { filterData } = useContext(FilterContext);
  const { settings } = useContext(FirebaseContext);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

  const uploadingCount =
    uploadNewFile?.list?.filter((file) => file.status === "uploading")
      ?.length || 0;

  const canceledCount =
    uploadNewFile?.list?.filter((file) => file.status === "canceled")?.length ||
    0;

  const failedCount =
    uploadNewFile?.list?.filter((file) => file.status === "failed")?.length ||
    0;

  const uploadedCount =
    uploadNewFile?.list?.filter((file) => file.status === "uploaded")?.length ||
    0;

  const totalFiles = uploadNewFile?.list?.length || 0;

  // Auto-close only when nothing is left that the user can still retry
  useEffect(() => {
    if (totalFiles > 0 && uploadingCount === 0 && uploadNewFile?.display) {
      const hasRetryable = uploadNewFile.list.some(
        (f) => f.status === "canceled" || f.status === "failed",
      );
      if (hasRetryable) return;

      const timer = setTimeout(() => {
        dispatch(clearUploadFiles());
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [
    totalFiles,
    uploadingCount,
    uploadNewFile?.display,
    uploadNewFile?.list,
    dispatch,
  ]);

  let headerLabel = t("uploadLoader.uploadingOfFiles", { current: uploadingCount, total: totalFiles });
  let headerCount = uploadingCount;

  if (uploadingCount > 0) {
    headerLabel = t(
      totalFiles === 1 ? "uploadLoader.uploadingOfFile" : "uploadLoader.uploadingOfFiles",
      { current: uploadingCount, total: totalFiles }
    );
    headerCount = uploadingCount;
  } else if (canceledCount > 0 || failedCount > 0) {
    if (canceledCount > 0) {
      headerLabel = t("uploadLoader.cancelledCap");
      headerCount = canceledCount;
    } else {
      headerLabel = t("uploadLoader.failed");
      headerCount = failedCount;
    }
  } else if (uploadedCount === totalFiles && totalFiles > 0) {
    headerLabel = t(
      uploadedCount === 1 ? "uploadLoader.uploadedFile" : "uploadLoader.uploadedFiles",
      { count: uploadedCount }
    );
    headerCount = uploadedCount;
  }

  const handleUploadError = (res) => {
    if (res === 2005) {
      dispatch(
        openToast({
          type: "error",
          message: t("errorCode.2005", { value: settings?.maxFileSize }),
        }),
      );
    } else if (res === 2001) {
      dispatch(openToast({ type: "error", message: t("errorCode.2001") }));
    } else {
      dispatch(
        openToast({ type: "error", message: t("validation.uploadFailed") }),
      );
    }
  };

  const handleRetry = (item) => {
    if (!item?.fileRef || !item.tempId) {
      console.warn("Cannot re-upload: fileRef is missing", item);
      return;
    }

    const formData = new FormData();
    formData.append("files", item.fileRef);
    formData.append("type", getFileExtension(item.fileRef));

    const effectiveFolderId = item.folderId ?? folderId;
    if (effectiveFolderId) formData.append("folderId", effectiveFolderId);
    if (item.isReplaced) formData.append("isReplaced", "true");

    dispatch(
      UploadFilesAction({
        formData,
        tempId: item.tempId,
        oldFileId: item.isReplaced ? item.oldFileId : null,
        sortBy: filterData?.sortBy,
        sortOrder: filterData?.sortOrder,
        onSuccess: () => {
          if (folderId == null) {
            navigate("/drive/mydrive");
          }
        },
        onError: handleUploadError,
      }),
    );
  };

  useEffect(() => {
    if (isCancelModalOpen && uploadingCount === 0) {
      dispatch(closeCenterModal());
      setIsCancelModalOpen(false);
    }
  }, [isCancelModalOpen, uploadingCount, dispatch]);

  return (
    <MuiBox
      sx={{
        width: "100%",
        bgcolor: "background.paper",
        borderRadius: 1,
        boxShadow: 24,
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <MuiBox
        sx={{
          display: "flex",
          gap: "5px",
          width: "100%",
          p: "5px",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "background.blue400",
        }}
      >
        <MuiTypography
          variant="16"
          sx={{ fontWeight: 600, px: 1 }}
        >
          {headerLabel}
        </MuiTypography>

        <MuiBox
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
            width: "fit-content",
          }}
        >
          {uploadingCount > 0 && (
            <Button
              size="small"
              onClick={() => {
                setIsCancelModalOpen(true);
                dispatch(
                  openCenterModal({
                    content: (
                      <CancelUploadModal
                        title={t("uploadLoader.cancelAll")}
                        description={t("uploadLoader.areYouSureCancelAllUploads")}
                        cancelBtnName={t("uploadLoader.cancelUpload")}
                        continueBtnName={t("uploadLoader.continueUploading")}
                        onCancelUploads={() => {
                          dispatch(cancelUploadingAllFiles());
                          abortAllUploads();
                          setIsCancelModalOpen(false);
                        }}
                        onContinue={() => setIsCancelModalOpen(false)}
                      />
                    ),
                  }),
                );
              }}
              sx={{
                minWidth: "auto",
                px: "8px",
                fontSize: "14px",
                textTransform: "none",
              }}
            >
              {t("commonButton.cancel")}
            </Button>
          )}
          {uploadingCount === 0 && (
            <IconButton
              size="small"
              onClick={() => dispatch(clearUploadFiles())}
            >
              <CloseIcon sx={{ fontSize: 20 }} />
            </IconButton>
          )}
        </MuiBox>
      </MuiBox>

      {/* Body */}
      <MuiBox
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          p: 2,
          maxHeight: "40vh",
          overflowY: "auto",
          overflowX: "hidden",
          minHeight: 0,
        }}
        className="no-scrollbar"
      >
        <MuiBox
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            width: "100%",
            minWidth: 0,
          }}
        >
          {uploadNewFile?.list?.map((item, idx) => (
            <MuiBox
              key={item.tempId || `${item.name}-${idx}`}
              sx={{ width: "100%", mb: 1, flexShrink: 0 }}
            >
              <MuiBox
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  minWidth: 0,
                }}
              >
                {/* Left – icon + name */}
                <MuiBox
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    minWidth: 0,
                    flex: 1,
                  }}
                >
                  {item.status === "uploaded" && (
                    <CheckCircleIcon
                      color="success"
                      sx={{ fontSize: 20, flexShrink: 0 }}
                    />
                  )}
                  {item.status === "failed" && (
                    <DoNotDisturbOnIcon
                      color="error"
                      sx={{ fontSize: 20, flexShrink: 0 }}
                    />
                  )}
                  {item.status === "canceled" && (
                    <CancelIcon
                      color="warning"
                      sx={{ fontSize: 20, flexShrink: 0 }}
                    />
                  )}

                  <MuiTypography
                    variant="14"
                    noWrap
                    sx={{
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {item.status === "uploading" &&
                      `${t("uploadLoader.uploadingLabel")}: `}
                    {item.status === "uploaded" &&
                      `${t("uploadLoader.uploadedCap")}: `}
                    {item.status === "failed" &&
                      `${t("uploadLoader.failed")}: `}
                    {item.status === "canceled" &&
                      `${t("uploadLoader.cancelledCap")}: `}
                    {item.name}
                  </MuiTypography>
                </MuiBox>

                {/* Right – actions */}
                <MuiBox
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    width: "fit-content",
                    flexShrink: 0,
                  }}
                >
                  {item.status === "uploading" && (
                    <>
                      <CircularProgress
                        enableTrackSlot
                        variant="determinate"
                        value={item.progress ?? 0}
                        size={22}
                        thickness={8}
                        sx={{
                          color: "primary.main",
                          "& .MuiCircularProgress-circle": {
                            strokeLinecap: "round",
                          },
                          borderRadius: "50%",
                          boxShadow: "inset 0 0 0 3.5px #BBDEFB",
                        }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => {
                          dispatch(cancelUploadingFile(item.tempId));
                          abortUpload(item.tempId);
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 20 }} />
                      </IconButton>
                    </>
                  )}

                  {/* Only Re-upload button for canceled / failed */}
                  {(item.status === "canceled" || item.status === "failed") && (
                    <span>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleRetry(item)}
                        disabled={!item.fileRef}
                      >
                        <img src={ReUploadIcon} alt="retry" style={{ width: "22px", height: "22px" }} />
                      </IconButton>
                    </span>
                  )}
                </MuiBox>
              </MuiBox>
            </MuiBox>
          ))}
        </MuiBox>
      </MuiBox>
    </MuiBox>
  );
};

export default UploadFileLoader;